import Layout from "@/components/Layout";
import { PageHero } from "@/components/PageHero";
import { PageDecorations } from "@/components/Decorations";
import { RichContentWithLinks } from "@/components/RichContentWithLinks";
import { usePageContent } from "@/lib/usePageContent";
import lottaPartioAsset from "@/assets/lotta-partio.jpg";
import lottaValmistuminenAsset from "@/assets/lotta-valmistuminen.jpg";
import lottaForeverAsset from "@/assets/lotta-forever.jpg";
import lottaPuheAsset from "@/assets/lotta-puhe.jpg";
import lottaKoiraAsset from "@/assets/lotta-koira.jpg";
import lottaEduskuntaAsset from "@/assets/lotta-eduskunta.jpg";

const FALLBACK = {
  hero_badge: "Tietoa minusta",
  hero_title: "Mie, Lotta Ellonen",
  section_1_title: "Mie oon Lotta Ellonen!",
  section_1_body: "",
  section_2_title: "",
  section_2_body: "",
  section_3_title: "",
  section_3_body: "",
  section_4_title: "",
  section_4_body: "",
  section_5_title: "",
  section_5_body: "",
  section_6_title: "",
  section_6_body: "",
  section_7_title: "",
  section_7_body: "",
  section_8_title: "",
  section_8_body: "",
};

const SECTION_BACKGROUNDS = [
  "hsl(38 45% 93%)",
  "hsl(206 42% 90%)",
  "hsl(340 28% 91%)",
  "hsl(42 80% 88%)",
  "hsl(195 40% 90%)",
  "hsl(20 45% 91%)",
  "hsl(206 42% 90%)",
  "hsl(38 45% 93%)",
];

const About = () => {
  const { t } = usePageContent("about", FALLBACK);

  const sections = Array.from({ length: 8 }, (_, i) => ({
    title: t(`section_${i + 1}_title` as keyof typeof FALLBACK),
    body: t(`section_${i + 1}_body` as keyof typeof FALLBACK),
    bg: SECTION_BACKGROUNDS[i],
    index: i,
  })).filter((s) => s.title || s.body);

  return (
    <Layout>
      <PageHero badge={t("hero_badge")}>{t("hero_title")}</PageHero>

      <section
        className="relative overflow-hidden px-6 py-16 lg:px-10"
        style={{ background: "linear-gradient(180deg, hsl(39,82%,92%) 0%, hsl(42,86%,78%) 100%)" }}
      >
        <PageDecorations />
        <div className="relative mx-auto max-w-3xl space-y-8">
          {sections.map((s, idx) => (
            <article
              key={idx}
              className="rounded-[1.6rem] border border-border/60 p-8 shadow-sm md:p-10"
              style={{ background: s.bg }}
            >
              {s.title && (
                <h2 className="mb-5 font-display text-2xl font-bold italic leading-snug text-secondary md:text-3xl">
                  {s.title}
                </h2>
              )}
              {s.body && s.index === 1 ? (() => {
                const paras = s.body.split(/\n\s*\n/);
                const first = paras[0] ?? "";
                const rest = paras.slice(1).join("\n\n");
                return (
                  <>
                    {first && (
                      <RichContentWithLinks
                        html={first}
                        className="space-y-4 text-lg leading-relaxed text-foreground/85"
                      />
                    )}
                    <div className="mt-6 clearfix">
                      <figure className="mb-4 mr-0 w-full sm:float-left sm:mr-7 sm:w-56 md:w-64">


                        <div className="rotate-[-2deg] bg-[#fdfaf3] p-3 pb-10 shadow-[0_8px_24px_-8px_rgba(60,40,20,0.35)] ring-1 ring-black/5">
                          <img
                            src={lottaPartioAsset}
                            alt="Nuori Lotta partiohuivi kaulassa"
                            loading="lazy"
                            className="block h-auto w-full"
                          />
                        </div>
                      </figure>
                      {rest && (
                        <RichContentWithLinks
                          html={rest}
                          className="space-y-4 text-lg leading-relaxed text-foreground/85"
                        />
                      )}
                      <div className="clear-both" />
                    </div>
                  </>
                );
              })() : s.body && s.index === 2 ? (() => {
                const paras = s.body.split(/\n\s*\n/);
                const first = paras[0] ?? "";
                const rest = paras.slice(1).join("\n\n");
                return (
                  <>
                    {first && (
                      <RichContentWithLinks
                        html={first}
                        className="space-y-4 text-lg leading-relaxed text-foreground/85"
                      />
                    )}
                    <div className="mt-5 flex flex-col gap-5 sm:flex-row sm:items-end sm:gap-6">
                      {rest && (
                        <RichContentWithLinks
                          html={rest}
                          className="flex-1 space-y-4 text-lg leading-relaxed text-foreground/85"
                        />
                      )}
                      <figure className="mx-auto w-1/2 shrink-0 sm:mx-0 sm:w-48 md:w-56">
                        <div className="rotate-[2deg] bg-[#fdfaf3] p-3 pb-10 shadow-[0_8px_24px_-8px_rgba(60,40,20,0.35)] ring-1 ring-black/5">
                          <img
                            src={lottaValmistuminenAsset}
                            alt="Lotta ylioppilaslakki päässä, valkoinen ruusu ja ylioppilaan kansio kädessä"
                            loading="lazy"
                            className="block h-auto w-full"
                          />
                        </div>
                      </figure>
                    </div>
                  </>
                );
              })() : s.body && s.index === 3 ? (() => {
                // HTML body — split at first </p> to inject image after first paragraph
                const idx = s.body.indexOf("</p>");
                const first = idx >= 0 ? s.body.slice(0, idx + 4) : s.body;
                const rest = idx >= 0 ? s.body.slice(idx + 4) : "";
                return (
                  <>
                    {first && (
                      <RichContentWithLinks
                        html={first}
                        className="space-y-4 text-lg leading-relaxed text-foreground/85"
                      />
                    )}
                    <div className="mt-4 clearfix">
                      <figure className="mb-4 mr-0 w-full sm:float-left sm:mr-7 sm:w-56 md:w-64">
                        <div className="rotate-[-2deg] bg-[#fdfaf3] p-3 pb-10 shadow-[0_8px_24px_-8px_rgba(60,40,20,0.35)] ring-1 ring-black/5">
                          <img
                            src={lottaForeverAsset}
                            alt="Lotta Forever Living -tapahtumassa"
                            loading="lazy"
                            className="block h-auto w-full"
                          />
                        </div>
                      </figure>
                      {rest && (
                        <RichContentWithLinks
                          html={rest}
                          className="space-y-4 text-lg leading-relaxed text-foreground/85"
                        />
                      )}
                      <div className="clear-both" />
                    </div>
                  </>
                );
              })() : s.body && s.index === 4 ? (() => {
                const paras = s.body.split(/\n\s*\n/);
                const first = paras[0] ?? "";
                const rest = paras.slice(1).join("\n\n");
                return (
                  <>
                    {first && (
                      <RichContentWithLinks
                        html={first}
                        className="space-y-4 text-lg leading-relaxed text-foreground/85"
                      />
                    )}
                    <div className="mt-4 clearfix">
                      <figure className="mb-4 ml-0 w-full sm:float-right sm:ml-7 sm:w-56 md:w-64">
                        <div
                          className="rotate-[2deg] bg-[#fdfaf3] p-3 pb-10 shadow-[0_8px_24px_-8px_rgba(60,40,20,0.35)] ring-1 ring-black/5"
                          title="Kuvaaja: Ketola, Markus"
                        >
                          <img
                            src={lottaPuheAsset}
                            alt="Lotta puhumassa mikrofoniin tapahtumassa"
                            loading="lazy"
                            className="block h-auto w-full"
                          />
                        </div>
                      </figure>
                      {rest && (
                        <RichContentWithLinks
                          html={rest}
                          className="space-y-4 text-lg leading-relaxed text-foreground/85"
                        />
                      )}
                      <div className="clear-both" />
                    </div>
                  </>
                );
              })() : s.body && s.index === 6 ? (() => {
                const paras = s.body.split(/\n\s*\n/);
                const first = paras[0] ?? "";
                const rest = paras.slice(1).join("\n\n");
                return (
                  <>
                    {first && (
                      <RichContentWithLinks
                        html={first}
                        className="space-y-4 text-lg leading-relaxed text-foreground/85"
                      />
                    )}
                    <div className="mt-5 flex flex-col gap-5 sm:flex-row sm:items-end sm:gap-6">
                      {rest && (
                        <RichContentWithLinks
                          html={rest}
                          className="flex-1 space-y-4 text-lg leading-relaxed text-foreground/85"
                        />
                      )}
                      <figure className="mx-auto w-1/2 shrink-0 sm:mx-0 sm:w-40 md:w-48">

                        <div className="rotate-[2deg] bg-[#fdfaf3] p-3 pb-10 shadow-[0_8px_24px_-8px_rgba(60,40,20,0.35)] ring-1 ring-black/5">
                          <img
                            src={lottaEduskuntaAsset}
                            alt="Lotta eduskunnassa"
                            loading="lazy"
                            className="block h-auto w-full"
                          />
                        </div>
                      </figure>
                    </div>
                  </>
                );
              })() : s.body && s.index === 7 ? (
                <div className="flex flex-col-reverse gap-5 sm:flex-row sm:items-start sm:gap-6">
                  <figure className="mx-auto w-1/2 shrink-0 sm:mx-0 sm:w-40 md:w-48">
                    <div className="rotate-[-2deg] bg-[#fdfaf3] p-3 pb-10 shadow-[0_8px_24px_-8px_rgba(60,40,20,0.35)] ring-1 ring-black/5">
                      <img
                        src={lottaKoiraAsset}
                        alt="Lotta ja koira"
                        loading="lazy"
                        className="block h-auto w-full"
                      />
                    </div>
                  </figure>
                  <RichContentWithLinks
                    html={s.body}
                    className="flex-1 space-y-4 text-lg leading-relaxed text-foreground/85"
                  />
                </div>
              ) : s.body && (
                <RichContentWithLinks
                  html={s.body}
                  className="space-y-4 text-lg leading-relaxed text-foreground/85"
                />
              )}
            </article>
          ))}
        </div>
      </section>
    </Layout>
  );
};

export default About;
